import React, { useState } from 'react';
import { CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import { Calendar } from "../../components/ui/calendar"
import { format } from 'date-fns';
import { exportToPDF } from '../../utils/pdfExport';
import QRCode from 'qrcode';
import { Assignment } from '../../types';
import { AssignmentService } from '../../services/AssignmentService';
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Alert, AlertDescription } from "../../components/ui/alert"

interface AssignmentDownloadsProps {
  assignments: Assignment[];
  onAssignmentsUpdate: (newAssignments: Assignment[]) => void;
}

const AssignmentDownloads: React.FC<AssignmentDownloadsProps> = ({ assignments, onAssignmentsUpdate }) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);

  const handleExportAssignments = async () => {
    if (!selectedDate) return;

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    const assignmentsToExport = assignments.filter(assignment =>
      assignment.date && format(new Date(assignment.date), 'yyyy-MM-dd') === formattedDate
    );

    if (assignmentsToExport.length === 0) {
      alert('No assignments found for the selected date.');
      return;
    }

    // Generate QR code
    const qrCodeDataURL = await QRCode.toDataURL('http://tectiform.com');

    // Export to PDF with QR code
    exportToPDF(assignmentsToExport, `Assignments_${formattedDate}`, qrCodeDataURL);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const csv = e.target?.result as string;
      try {
        const result = await AssignmentService.processCSV(csv);
        onAssignmentsUpdate(result.newAssignments);
        setUploadStatus(`Successfully added ${result.newAssignments.length} new assignments. ${result.duplicates} duplicates were skipped.`);
      } catch (error) {
        setUploadStatus(`Error processing CSV: ${(error as Error).message}`);
      }
    };
    reader.readAsText(file);
  };

  return (
    <>
      <CardHeader>
        <CardTitle>Assignment Downloads and Uploads</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col space-y-4">
          <div className="flex items-center space-x-4 mb-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="rounded-md border"
            />
          </div>
          <div>
            <Button onClick={handleExportAssignments}>Export Assignments</Button>
          </div>

          <div>
            <Label htmlFor="csv-upload">Upload CSV</Label>
            <Input id="csv-upload" type="file" accept=".csv" onChange={handleFileUpload} />
          </div>

          {uploadStatus && (
            <Alert>
              <AlertDescription>{uploadStatus}</AlertDescription>
            </Alert>
          )}
        </div>
      </CardContent>
    </>
  );
};

export default AssignmentDownloads;